async function request(path, options = {}) {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...options,
    headers: {
      ...(options.body ? { "content-type": "application/json" } : {}),
      ...options.headers,
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error?.message || "The request could not be completed");
    error.code = data.error?.code;
    error.status = response.status;
    error.details = data.error?.details;
    throw error;
  }
  return data;
}

export const api = {
  me: () => request("/api/v1/auth/me"),
  login: (email, password) => request("/api/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  }),
  signup: (input) => request("/api/v1/auth/signup", {
    method: "POST",
    body: JSON.stringify(input),
  }),
  authProviders: () => request("/api/v1/auth/providers"),
  googleSignIn: (credential, options = {}) => request("/api/v1/auth/google", {
    method: "POST",
    body: JSON.stringify({
      credential,
      ...(options.password ? { password: options.password } : {}),
      ...(options.inviteToken ? { invite_token: options.inviteToken } : {}),
    }),
  }),
  onboarding: () => request("/api/v1/auth/onboarding"),
  selectWorkspace: (workspaceId) => request("/api/v1/auth/onboarding/select", {
    method: "POST",
    body: JSON.stringify({ workspace_id: workspaceId }),
  }),
  createWorkspace: (input) => request("/api/v1/auth/onboarding/workspaces", {
    method: "POST",
    body: JSON.stringify(input),
  }),
  requestWorkspaceJoin: (workspaceId) => request("/api/v1/auth/onboarding/join-requests", {
    method: "POST",
    body: JSON.stringify({ workspace_id: workspaceId }),
  }),
  invitation: (token) => request(`/api/v1/invitations/${encodeURIComponent(token)}`),
  acceptInvitation: (token) => request(`/api/v1/invitations/${encodeURIComponent(token)}/accept`, { method: "POST" }),
  logout: () => request("/api/v1/auth/logout", { method: "POST" }),
  members: () => request("/api/v1/users"),
  joinRequests: (status = "pending") => request(`/api/v1/workspace-join-requests?${new URLSearchParams({ status })}`),
  reviewJoinRequest: (id, action) => request(`/api/v1/workspace-join-requests/${encodeURIComponent(id)}/${action}`, { method: "POST" }),
  invitations: (status = "pending") => request(`/api/v1/workspace-invitations?${new URLSearchParams({ status })}`),
  createInvitation: (input) => request("/api/v1/workspace-invitations", {
    method: "POST",
    body: JSON.stringify(input),
  }),
  resendInvitation: (id) => request(`/api/v1/workspace-invitations/${encodeURIComponent(id)}/resend`, { method: "POST" }),
  revokeInvitation: (id) => request(`/api/v1/workspace-invitations/${encodeURIComponent(id)}/revoke`, { method: "POST" }),
  agentKeys: () => request("/api/v1/agent-keys"),
  createAgentKey: (input) => request("/api/v1/agent-keys", {
    method: "POST",
    body: JSON.stringify(input),
  }),
  revokeAgentKey: (id) => request(`/api/v1/agent-keys/${encodeURIComponent(id)}/revoke`, {
    method: "POST",
  }),
  companies: (params = {}) => request(`/api/v1/companies?${new URLSearchParams(params)}`),
  catalogCompanies: (query, limit = 20) => request(`/api/v1/catalog/companies?${new URLSearchParams({ q: query, limit })}`),
  platformCatalogOverview: () => request("/api/v1/platform/catalog/overview"),
  platformCatalogCompanies: (params = {}) => request(`/api/v1/platform/catalog/companies?${new URLSearchParams(params)}`),
  platformCatalogPeople: (params = {}) => request(`/api/v1/platform/catalog/people?${new URLSearchParams(params)}`),
  platformCatalogCompany: (id) => request(`/api/v1/platform/catalog/companies/${encodeURIComponent(id)}`),
  platformCatalogFields: () => request("/api/v1/platform/catalog/fields"),
  createPlatformCatalogField: (input) => request("/api/v1/platform/catalog/fields", {
    method: "POST",
    body: JSON.stringify(input),
  }),
  updatePlatformCatalogField: (id, input) => request(`/api/v1/platform/catalog/fields/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  }),
  platformCatalogRuns: (params = {}) => request(`/api/v1/platform/catalog/runs?${new URLSearchParams(params)}`),
  platformCatalogAudit: (params = {}) => request(`/api/v1/platform/catalog/audit?${new URLSearchParams(params)}`),
  company: (id) => request(`/api/v1/companies/${encodeURIComponent(id)}`),
  companyCatalog: (id) => request(`/api/v1/companies/${encodeURIComponent(id)}/catalog`),
  companyCatalogHistory: (id, fieldKey, limit = 50) => request(`/api/v1/companies/${encodeURIComponent(id)}/catalog/history?${new URLSearchParams({ field_key: fieldKey, limit })}`),
  resolveCompanyCatalog: (id, input) => request(`/api/v1/companies/${encodeURIComponent(id)}/catalog/resolve`, {
    method: "POST",
    body: JSON.stringify(input),
  }),
  companyMemory: (id, includeInactive = false) => request(`/api/v1/companies/${encodeURIComponent(id)}/memory?include_inactive=${includeInactive}`),
  createCompany: (input) => request("/api/v1/companies", {
    method: "POST",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(input),
  }),
  updateCompany: (id, input) => request(`/api/v1/companies/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(input),
  }),
  people: (params = {}) => request(`/api/v1/people?${new URLSearchParams(params)}`),
  person: (id) => request(`/api/v1/people/${encodeURIComponent(id)}`),
  createPerson: (input) => request("/api/v1/people", {
    method: "POST",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(input),
  }),
  linkPerson: (companyId, input) => request(`/api/v1/companies/${encodeURIComponent(companyId)}/people`, {
    method: "POST",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(input),
  }),
  addFact: (input) => request("/api/v1/facts", {
    method: "POST",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(input),
  }),
  facts: (params = {}) => request(`/api/v1/facts?${new URLSearchParams(params)}`),
  communications: (params = {}) => request(`/api/v1/communications?${new URLSearchParams(params)}`),
  communication: (id) => request(`/api/v1/communications/${encodeURIComponent(id)}`),
  ingestCommunication: (input) => request("/api/v1/communications", {
    method: "POST", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify(input),
  }),
  linkCommunication: (id, input) => request(`/api/v1/communications/${encodeURIComponent(id)}/link`, {
    method: "POST", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify(input),
  }),
  signals: (params = {}) => request(`/api/v1/signals?${new URLSearchParams(params)}`),
  signalSummary: () => request("/api/v1/signals/summary"),
  updateSignal: (id, input) => request(`/api/v1/signals/${encodeURIComponent(id)}`, {
    method: "PATCH", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify(input),
  }),
  timeline: (params = {}) => request(`/api/v1/timeline?${new URLSearchParams(params)}`),
  tasks: (params = {}) => request(`/api/v1/tasks?${new URLSearchParams(params)}`),
  task: (id) => request(`/api/v1/tasks/${encodeURIComponent(id)}`),
  createTask: (input) => request("/api/v1/tasks", {
    method: "POST",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(input),
  }),
  updateTask: (id, input) => request(`/api/v1/tasks/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(input),
  }),
  salesPipelines: (params = {}) => request(`/api/v1/sales-pipelines?${new URLSearchParams(params)}`),
  createSalesPipeline: (input) => request("/api/v1/sales-pipelines", {
    method: "POST", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify(input),
  }),
  createSalesPipelineStage: (pipelineId, input) => request(`/api/v1/sales-pipelines/${encodeURIComponent(pipelineId)}/stages`, {
    method: "POST", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify(input),
  }),
  updateSalesPipelineStage: (stageId, input) => request(`/api/v1/sales-pipeline-stages/${encodeURIComponent(stageId)}`, {
    method: "PATCH", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify(input),
  }),
  leads: (params = {}) => request(`/api/v1/leads?${new URLSearchParams(params)}`),
  lead: (id) => request(`/api/v1/leads/${encodeURIComponent(id)}`),
  deals: (params = {}) => request(`/api/v1/deals?${new URLSearchParams(params)}`),
  promoteLead: (id, input) => request(`/api/v1/leads/${encodeURIComponent(id)}/promote`, {
    method: "POST", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify(input),
  }),
  salesSettings: () => request("/api/v1/sales-settings"),
  updateSalesSettings: (input) => request("/api/v1/sales-settings", {
    method: "PATCH", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify(input),
  }),
  createLead: (input) => request("/api/v1/leads", {
    method: "POST", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify(input),
  }),
  updateLead: (id, input) => request(`/api/v1/leads/${encodeURIComponent(id)}`, {
    method: "PATCH", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify(input),
  }),
  archiveLead: (id) => request(`/api/v1/leads/${encodeURIComponent(id)}/archive`, {
    method: "POST", headers: { "idempotency-key": crypto.randomUUID() },
  }),
  restoreLead: (id) => request(`/api/v1/leads/${encodeURIComponent(id)}/restore`, {
    method: "POST", headers: { "idempotency-key": crypto.randomUUID() },
  }),
  changeLeadStage: (id, input) => request(`/api/v1/leads/${encodeURIComponent(id)}/stage`, {
    method: "POST", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify(input),
  }),
  reorderLead: (id, position) => request(`/api/v1/leads/${encodeURIComponent(id)}/reorder`, {
    method: "POST", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify({ position }),
  }),
  addLeadPerson: (id, input) => request(`/api/v1/leads/${encodeURIComponent(id)}/people`, {
    method: "POST", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify(input),
  }),
  updateLeadPerson: (id, linkId, input) => request(`/api/v1/leads/${encodeURIComponent(id)}/people/${encodeURIComponent(linkId)}`, {
    method: "PATCH", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify(input),
  }),
  removeLeadPerson: (id, linkId) => request(`/api/v1/leads/${encodeURIComponent(id)}/people/${encodeURIComponent(linkId)}/remove`, {
    method: "POST", headers: { "idempotency-key": crypto.randomUUID() },
  }),
  leadTimeline: (id, params = {}) => request(`/api/v1/leads/${encodeURIComponent(id)}/timeline?${new URLSearchParams(params)}`),
  createLeadFromListRow: (listId, rowId, input) => request(`/api/v1/lists/${encodeURIComponent(listId)}/rows/${encodeURIComponent(rowId)}/create-lead`, {
    method: "POST", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify(input),
  }),
  personCompanyContexts: (params = {}) => request(`/api/v1/person-company-contexts?${new URLSearchParams(params)}`),
  updatePersonCompanyContext: (companyId, personId, input) => request(`/api/v1/person-company-contexts/${encodeURIComponent(companyId)}/${encodeURIComponent(personId)}`, {
    method: "PATCH",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(input),
  }),
  updateFactStatus: (id, input) => request(`/api/v1/facts/${encodeURIComponent(id)}/status`, {
    method: "PATCH",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(input),
  }),
  fields: (entityType, params = {}) => request(`/api/v1/fields?${new URLSearchParams({
    ...(entityType ? { entity_type: entityType } : {}),
    ...params,
  })}`),
  createField: (input) => request("/api/v1/fields", {
    method: "POST",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(input),
  }),
  fieldValues: (params = {}) => request(`/api/v1/field-values?${new URLSearchParams(params)}`),
  setFieldValue: (input) => request("/api/v1/field-values", {
    method: "POST",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(input),
  }),
  fieldValueHistory: (params) => request(`/api/v1/field-values/history?${new URLSearchParams(params)}`),
  lists: () => request("/api/v1/lists"),
  list: (id, params = {}) => request(`/api/v1/lists/${encodeURIComponent(id)}?${new URLSearchParams(params)}`),
  listRows: (id, params = {}) => request(`/api/v1/lists/${encodeURIComponent(id)}/rows?${new URLSearchParams(params)}`),
  listPeople: (id, companyId) => request(`/api/v1/lists/${encodeURIComponent(id)}/rows/${encodeURIComponent(companyId)}/people`),
  createList: (input) => request("/api/v1/lists", {
    method: "POST",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(input),
  }),
  updateList: (id, input) => request(`/api/v1/lists/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(input),
  }),
  addListRow: (id, input) => request(`/api/v1/lists/${encodeURIComponent(id)}/rows`, {
    method: "POST",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(input),
  }),
  addListColumn: (id, input) => request(`/api/v1/lists/${encodeURIComponent(id)}/columns`, {
    method: "POST",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(input),
  }),
  updateListRow: (id, input) => request(`/api/v1/list-rows/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(input),
  }),
  emailSettings: () => request("/api/v1/email-settings"),
  updateEmailSettings: (input) => request("/api/v1/email-settings", {
    method: "PATCH",
    body: JSON.stringify(input),
  }),
  emailDrafts: (params = {}) => request(`/api/v1/email-drafts?${new URLSearchParams(params)}`),
  createEmailDraft: (input) => request("/api/v1/email-drafts", {
    method: "POST",
    body: JSON.stringify(input),
  }),
  updateEmailDraft: (id, input) => request(`/api/v1/email-drafts/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  }),
  approveEmail: (approvalId) => request(`/api/v1/email-approvals/${encodeURIComponent(approvalId)}/approve`, {
    method: "POST",
  }),
  cancelEmail: (approvalId) => request(`/api/v1/email-approvals/${encodeURIComponent(approvalId)}/cancel`, {
    method: "POST",
  }),
  suppressions: (params = {}) => request(`/api/v1/suppressions?${new URLSearchParams(params)}`),
  revokeSuppression: (id) => request(`/api/v1/suppressions/${encodeURIComponent(id)}/revoke`, {
    method: "POST",
  }),
  workspaceDocuments: (params = {}) => request(`/api/v1/workspace-documents?${new URLSearchParams(params)}`),
  workspaceDocument: (id) => request(`/api/v1/workspace-documents/${encodeURIComponent(id)}`),
  createWorkspaceDocument: (input) => request("/api/v1/workspace-documents", {
    method: "POST",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(input),
  }),
  updateWorkspaceDocument: (id, input) => request(`/api/v1/workspace-documents/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(input),
  }),
  workspaceDocumentHistory: (id) => request(`/api/v1/workspace-documents/${encodeURIComponent(id)}/history`),
  mcpActivity: (params = {}) => request(`/api/v1/activity/mcp?${new URLSearchParams(params)}`),
  mcpActivityDetail: (id) => request(`/api/v1/activity/mcp/${encodeURIComponent(id)}`),
  enrichmentActivity: (params = {}) => request(`/api/v1/activity/enrichment?${new URLSearchParams(params)}`),
  enrichmentActivityDetail: (id) => request(`/api/v1/activity/enrichment/${encodeURIComponent(id)}`),
  applyEnrichmentResult: (id) => request(`/api/v1/activity/enrichment/${encodeURIComponent(id)}/apply`, {
    method: "POST",
  }),
};
